import { useState } from 'react';
import { Info, LockSimple, ArrowRight } from '@phosphor-icons/react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { SimpleTooltip, IconTooltipButton } from '../../../components/ui/tooltip';
import type { ExpectedField, ImportBatchRow } from '@discovr/types';
import { RowNumberBadge } from './ImportRowBadge';
import { SegmentedControl, type SegmentOption } from './SegmentedControl';
import { compactFieldLabel, rosterComparisonFields } from './importWizardHelpers';
import { isRejectedStatus } from './helpers';
import { cn } from '../../../lib/utils';
import { TruncatedText } from '../../../components/common/TruncatedText';

/** One value cell of the merge comparison table — reveals the full value on
 *  hover only when the fixed-width column actually clips it. `value` may be
 *  empty, in which case a '—' placeholder shows and no tooltip opens. */
function MergeCell({ value, className }: { value: string; className?: string }) {
  return (
    <td className={cn('px-2 py-1.5', className)}>
      <TruncatedText text={value} placeholder="—" className="block" />
    </td>
  );
}

const LOCKED_EXPLANATION =
  'This creator has moved past pending review, so their deliverable prices are locked. Overwrite and Merge will still update their stats, but never their prices.';
const REMOVED_OR_REJECTED_EXPLANATION =
  'This creator was removed or rejected earlier, so a re-import will not change their status or prices — only their stats. Use Send back to Pending on the Rejected tab to reopen them.';
const MERGE_EXPLANATION =
  'Checked = take the sheet\'s value for this field. Unchecked = keep what\'s already on the roster. Starts fully checked, same as a plain Overwrite.';

interface RosterConflictCardProps {
  row: ImportBatchRow;
  expectedFields: ExpectedField[];
  busy: boolean;
  onResolve: (
    rowNumber: number,
    action: 'keep_existing' | 'overwrite' | 'skip',
    keepFields?: string[],
  ) => Promise<void>;
}

/** One horizontal line of the comparison — every field inline on a single
 *  row ("Current: ..." / "Sheet: ..."), not one row per field, so the card
 *  stays compact in a list of many. Only the Sheet row's `highlightChanged`
 *  values stand out — the Current row is always plain. */
function ComparisonLine({
  label,
  fields,
  values,
  highlightChanged,
}: {
  label: string;
  fields: ExpectedField[];
  values: (f: ExpectedField) => { text: string; changed: boolean };
  highlightChanged: boolean;
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-1 text-[11px] leading-relaxed">
      <span className="shrink-0 font-semibold text-muted-foreground">{label}</span>
      {fields.map((f, i) => {
        const { text, changed } = values(f);
        return (
          <span key={f.key} className={cn(highlightChanged && changed ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
            {i > 0 && <span className="text-border">· </span>}
            {compactFieldLabel(f)} {text}
          </span>
        );
      })}
    </div>
  );
}

/** Per-field merge picker — a real table (unlike the 2-line comparison
 *  above): one row per comparable field, a checkbox choosing sheet vs.
 *  roster, and a Result column that reflects the choice in plain words.
 *  Column layout follows the upload-sheet prototype: Field · Current in
 *  system · → · From sheet · Result. Checked = take the sheet's value (Will
 *  change); unchecked = keep the roster's current value (Kept). The initial
 *  checked set comes from the row's saved merge decision, so re-opening a
 *  merge restores exactly what was picked before. */
function MergeTable({
  fields,
  currentValues,
  sheetValues,
  checkedKeys,
  onToggle,
}: {
  fields: ExpectedField[];
  currentValues: Record<string, string>;
  sheetValues: Record<string, string>;
  checkedKeys: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    // table-layout: fixed + a colgroup with fixed percentage widths — without
    // it the browser recomputes each column's intrinsic width from its
    // widest cell on every render (auto layout), and "Will change" vs. "Kept"
    // in the Result column differ just enough to visibly shift the whole table
    // sideways on every checkbox toggle.
    <table
      className="w-full border-collapse overflow-hidden rounded-lg border border-border text-[11px]"
      style={{ tableLayout: 'fixed' }}
    >
      <colgroup>
        <col style={{ width: '9%' }} />
        <col style={{ width: '18%' }} />
        <col style={{ width: '25%' }} />
        <col style={{ width: '6%' }} />
        <col style={{ width: '25%' }} />
        <col style={{ width: '17%' }} />
      </colgroup>
      <thead>
        <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
          <th className="px-2 py-1.5">
            <SimpleTooltip content={MERGE_EXPLANATION}>
              <IconTooltipButton label="What does the merge checkbox do?">
                <Info size={12} />
              </IconTooltipButton>
            </SimpleTooltip>
          </th>
          <th className="truncate px-2 py-1.5 font-medium">Field</th>
          <th className="truncate px-2 py-1.5 font-medium">Current in system</th>
          <th className="px-2 py-1.5" aria-hidden="true" />
          <th className="truncate px-2 py-1.5 font-medium">From sheet</th>
          <th className="truncate px-2 py-1.5 font-medium">Result</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((f) => {
          const checked = checkedKeys.has(f.key);
          return (
            <tr key={f.key} className="border-b border-border last:border-b-0">
              <td className="px-2 py-1.5">
                <Checkbox checked={checked} onCheckedChange={() => onToggle(f.key)} aria-label={`Take ${compactFieldLabel(f)} from sheet`} />
              </td>
              <MergeCell value={compactFieldLabel(f)} className="font-medium text-foreground" />
              <MergeCell value={currentValues[f.key] || ''} className="text-muted-foreground" />
              <td className="px-2 py-1.5 text-center text-muted-foreground">
                <ArrowRight size={12} aria-hidden="true" />
              </td>
              <MergeCell value={sheetValues[f.key] || ''} className={cn(checked ? 'text-foreground' : 'text-muted-foreground')} />
              <td className={cn('truncate px-2 py-1.5 font-medium', checked ? 'text-foreground' : 'text-muted-foreground')}>
                {checked ? 'Will change' : 'Kept'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** One row matched to an existing roster entry — shows what would actually
 *  change (current roster value vs. the sheet's incoming value) before the
 *  admin picks Overwrite / Skip / Merge, as two compact horizontal lines
 *  rather than a per-field table. Locked entries drop the deliverable-price
 *  fields from the comparison (Overwrite never touches them — see
 *  rosterComparisonFields) and get a small lock badge instead of text.
 *  Merge expands the comparison into a real per-field table so the admin
 *  can pick sheet vs. roster value field by field, submitted as an
 *  'overwrite' with the unchecked fields sent back as `keepFields`. Skip
 *  leaves the roster entry untouched — same effect as the old "Keep
 *  existing", just named for what it actually does to the row.
 *
 *  When every comparable field already matches, the BE auto-resolves it as
 *  skip at classification time (see rosterMatchIsIdentical server-side)
 *  before this ever renders — there's nothing to actually choose between AND
 *  nothing being imported for this row, so this component shows just the
 *  username and an "Already on roster — skipped" pill, no stats and no
 *  Overwrite/Skip/Merge buttons — there's genuinely nothing here worth
 *  reviewing. */
export function RosterConflictCard({ row, expectedFields, busy, onResolve }: RosterConflictCardProps) {
  const resolution = row.rosterResolution;
  const locked = row.rosterMatch?.locked ?? false;
  const lockedExplanation = isRejectedStatus(row.rosterMatch?.status ?? '')
    ? REMOVED_OR_REJECTED_EXPLANATION
    : LOCKED_EXPLANATION;
  const currentValues = row.rosterMatch?.currentValues ?? {};
  const fields = rosterComparisonFields(expectedFields, locked, currentValues, row.values);
  // Only the fields that actually differ — a field where the sheet and the
  // roster already agree isn't a real difference to show or to offer a
  // per-field merge choice on (checking/unchecking it would produce the same
  // value either way). `fields` (the full comparable set) is still needed
  // for allFieldsMatch below, which has to know whether ANYTHING was even
  // comparable, not just what differs.
  const changedFields = fields.filter((f) => (currentValues[f.key] || '') !== (row.values[f.key] || ''));
  const hasChanges = changedFields.length > 0;
  // Distinct from `fields.length === 0` (nothing known on either side, e.g. a
  // brand-new sheet with no roster data yet) — this is the case where real
  // values exist on both sides and they happen to match.
  const allFieldsMatch = fields.length > 0 && !hasChanges;
  // A saved merge is an overwrite that keeps at least one field from the
  // roster — distinct from a plain "take everything" Overwrite. Used to
  // restore the merge picker's checkboxes and to badge the active choice.
  const savedKeepFields = row.rosterKeepFields ?? [];
  const isSavedMerge = resolution === 'overwrite' && savedKeepFields.length > 0;
  // Checked = take the sheet's value. Initialize from the saved decision:
  // every changed field is checked EXCEPT the ones the row already keeps from
  // the roster, so re-opening a merge shows exactly what was picked before.
  const initialChecked = () => new Set(changedFields.filter((f) => !savedKeepFields.includes(f.key)).map((f) => f.key));
  const [merging, setMerging] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(initialChecked);

  const openMerge = () => {
    setCheckedKeys(initialChecked());
    setMerging(true);
  };
  const toggleField = (key: string) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const selectAllChanged = () => setCheckedKeys(new Set(changedFields.map((f) => f.key)));
  const clearAll = () => setCheckedKeys(new Set());
  const applyMerge = () => {
    const keepFields = changedFields.filter((f) => !checkedKeys.has(f.key)).map((f) => f.key);
    onResolve(row.rowNumber, 'overwrite', keepFields)
      .then(() => setMerging(false))
      .catch(() => {});
  };

  // The single Overwrite / Skip / Merge segmented control that sits at the top
  // right of the row (Merge only offered when there's something to merge).
  const segmentOptions: SegmentOption[] = [
    { key: 'overwrite', label: 'Overwrite' },
    { key: 'skip', label: 'Skip' },
    ...(hasChanges ? [{ key: 'merge', label: 'Merge' }] : []),
  ];
  // Which segment reads as active: the open merge editor and a saved merge both
  // show Merge; a plain overwrite (no kept fields) shows Overwrite; else the
  // recorded resolution, or nothing when still undecided.
  const activeSegment = merging || isSavedMerge ? 'merge' : resolution === 'overwrite' || resolution === 'skip' ? resolution : '';
  const onSegment = (key: string) => {
    if (key === 'merge') {
      if (!merging) openMerge();
      return;
    }
    // Narrow by literal comparison rather than casting, so adding a segment key
    // later can't silently POST an invalid roster_resolution action.
    if (key === 'overwrite' || key === 'skip') {
      setMerging(false);
      onResolve(row.rowNumber, key).catch(() => {});
    }
  };

  // Username on the left, the row's action control (or status pill) on the
  // right — everything about this row's state readable from one glance at this
  // single line, and the Overwrite / Skip / Merge choice lives here at the top
  // rather than as a separate button row at the bottom of the card.
  function renderHeader(right?: React.ReactNode) {
    return (
      <div className="mb-2 flex items-center gap-2 font-semibold text-foreground">
        <RowNumberBadge n={row.rowNumber} />
        {row.values.username ?? ''}
        {locked && (
          <SimpleTooltip content={lockedExplanation}>
            <IconTooltipButton
              label="Locked — deliverable prices won't change"
              className="size-5 shrink-0 bg-rose-50"
            >
              <LockSimple size={11} weight="fill" className="text-rose-600" />
            </IconTooltipButton>
          </SimpleTooltip>
        )}
        {right && <span className="ml-auto">{right}</span>}
      </div>
    );
  }

  if (allFieldsMatch && resolution !== '') {
    // Nothing is actually being imported for this row — the sheet and the
    // roster already agree on everything, so there's nothing worth
    // reviewing here either. Just the username and the pill; no stats.
    //
    // Also gated on `resolution !== ''`, not allFieldsMatch alone: the BE's
    // rosterMatchIsIdentical is the real source of truth for auto-resolving
    // a row (and it's what actually sets rosterResolution) — allFieldsMatch
    // here is this component's own client-side re-derivation from
    // expectedFields/currentValues/row.values, which SHOULD always agree
    // with it but isn't the same code path. If they ever disagreed (a bug,
    // a comparison-rule drift between the two), an unresolved row would
    // otherwise get hidden behind this read-only "skipped" pill with no
    // Overwrite/Skip/Merge buttons and no way to ever resolve it. Falling
    // through to the normal interactive view below is always safe.
    return (
      <div className="rounded-xl border border-border bg-card p-3 text-[12px]">
        {renderHeader(
          <span className="inline-flex h-5 shrink-0 items-center rounded-full bg-muted px-2.5 text-caption-medium text-muted-foreground">
            Already on roster — skipped
          </span>,
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-3 text-[12px]">
      {renderHeader(
        <SegmentedControl
          ariaLabel={`Resolve ${row.values.username ?? `row ${row.rowNumber}`}`}
          options={segmentOptions}
          value={activeSegment}
          onChange={onSegment}
          disabled={busy}
        />,
      )}

      {hasChanges && !merging && (
        <div className="mb-3 flex flex-col gap-1 rounded-lg border border-border bg-muted/30 px-3 py-2">
          <ComparisonLine
            label="Current"
            fields={changedFields}
            highlightChanged={false}
            values={(f) => ({ text: currentValues[f.key] || '—', changed: false })}
          />
          <ComparisonLine
            label="Sheet"
            fields={changedFields}
            highlightChanged
            values={(f) => {
              const current = currentValues[f.key] || '—';
              const next = row.values[f.key] || '—';
              return { text: next, changed: current !== next };
            }}
          />
        </div>
      )}

      {hasChanges && merging && (
        <div className="mb-3 flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-caption-medium text-muted-foreground">Tick the fields to take from the sheet</span>
            <div className="flex items-center gap-2">
              <Button type="button" size="sm" variant="outline" disabled={busy} onClick={selectAllChanged}>
                Select all changed
              </Button>
              <Button type="button" size="sm" variant="outline" disabled={busy} onClick={clearAll}>
                Clear
              </Button>
            </div>
          </div>
          <MergeTable fields={changedFields} currentValues={currentValues} sheetValues={row.values} checkedKeys={checkedKeys} onToggle={toggleField} />
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => setMerging(false)}>
              Cancel
            </Button>
            <Button type="button" size="sm" variant="default" disabled={busy} onClick={applyMerge}>
              Apply merge
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
